import React, { useMemo } from 'react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Area,
  AreaChart,
} from 'recharts';
import SubTabs from '../../components/SubTabs/SubTabs';
import InlineEmpty from '../../components/InlineEmpty/InlineEmpty';
import {
  AreaGradient,
  CHART_MARGIN,
  ChartFrame,
  ChartTooltip,
  CURSOR_PROPS,
  GRID_PROPS,
  X_AXIS_PROPS,
  Y_AXIS_PROPS,
  areaProps,
  compactTick,
  dateTick,
  seriesColor,
} from '../../components/charts/chartTheme';
import { useI18n } from '../../i18n/I18nContext';
import { formatDate, formatMoney, formatMoneyCompact } from '../../utils/datetime';
import { hasRevenue, niceAxis, seriesTotal } from './revenueSeries';

const PERIODS = [
  { key: 'd7', value: '7d' },
  { key: 'd30', value: '30d' },
  { key: 'd90', value: '90d' },
  { key: 'y1', value: '1y' },
];

const GRADIENT_ID = 'dashRevenueFill';

// A label such as "750 ming" or "1.2 mlrd" is about 55px wide at 12px and the axis keeps 14px of its
// width for the margin, so the shared 56px axis cut the first digit off. Until chartTheme's
// Y_AXIS_PROPS.width grows, this chart asks for the room itself.
const Y_AXIS_WIDTH = 72;

const moneyTip = (value) => formatMoney(value, { free: false });
const dayTip = (key) => formatDate(key, { year: 'auto' });

// Recharts draws the line in with its own timer, not a CSS animation, so the reduced-motion rule in
// index.css never reaches it: a visitor who asked for less motion gets the finished chart.
const prefersReducedMotion = () => (
  typeof window === 'undefined'
  || typeof window.matchMedia !== 'function'
  || window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

/**
 * Revenue of the selected period, one point per day (a day without sales is 0). The period picker
 * lives here, not in the page header: it changes this chart and nothing else on the page, and the
 * total above the chart follows the same period (and names the unit the axis leaves out).
 *
 * `status`: 'loading' | 'ready' | 'error'. `series`: [{ key: 'YYYY-MM-DD', value }].
 */
function RevenueCard({ period, onPeriodChange, status, series, onRetry }) {
  const { t } = useI18n();
  const tabs = PERIODS.map((p) => ({ key: p.value, label: t(`dashboard.periods.${p.key}`) }));
  const total = seriesTotal(series);
  const axis = useMemo(() => niceAxis(Math.max(0, ...series.map((point) => point.value))), [series]);

  let body;
  if (status === 'loading') {
    // The shape of the loaded card (the total line, then the chart), so nothing below it jumps.
    body = (
      <div aria-hidden="true">
        <div className="ui-skel dash-revenue__skel-total" />
        <div className="ui-skel dash-revenue__skel" />
      </div>
    );
  } else if (status === 'error') {
    body = (
      <InlineEmpty
        kind="error"
        title={t('dashboard.revenueError')}
        action={(
          <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={onRetry}>
            {t('ui.retryAgain')}
          </button>
        )}
      />
    );
  } else if (!hasRevenue(series)) {
    body = (
      <InlineEmpty
        kind="empty"
        title={t('dashboard.emptyRevenue.title')}
        description={t('dashboard.emptyRevenue.desc')}
      />
    );
  } else {
    body = (
      <>
        <p className="dash-revenue__total" title={moneyTip(total)}>
          {formatMoneyCompact(total)}
        </p>
        <ChartFrame data={series}>
          <AreaChart data={series} margin={CHART_MARGIN}>
            <defs>
              <AreaGradient id={GRADIENT_ID} color={seriesColor(0)} />
            </defs>
            <CartesianGrid {...GRID_PROPS} />
            <XAxis dataKey="key" {...X_AXIS_PROPS} tickFormatter={dateTick} />
            <YAxis
              {...Y_AXIS_PROPS}
              width={Y_AXIS_WIDTH}
              domain={[0, axis.top]}
              ticks={axis.ticks}
              tickFormatter={compactTick}
            />
            <Tooltip
              content={<ChartTooltip valueFormatter={moneyTip} labelFormatter={dayTip} />}
              cursor={CURSOR_PROPS}
            />
            <Area dataKey="value" {...areaProps(0, GRADIENT_ID)} isAnimationActive={!prefersReducedMotion()} />
          </AreaChart>
        </ChartFrame>
      </>
    );
  }

  return (
    <section className="section-card dash-revenue" aria-labelledby="dash-revenue-title" aria-busy={status === 'loading'}>
      <div className="section-head dash-revenue__head">
        <div>
          <h2 className="section-head__title" id="dash-revenue-title">{t('dashboard.revenueOverview')}</h2>
          <p className="section-head__desc">{t('dashboard.revenueSub')}</p>
        </div>
        <SubTabs
          tabs={tabs}
          active={period}
          onChange={onPeriodChange}
          ariaLabel={t('dashboard.selectPeriod')}
          className="dash-period"
        />
      </div>
      {body}
    </section>
  );
}

export default RevenueCard;
